import { GroupedMultiSelectGroup } from '@components/new/GroupedMultiSelect/GroupedMultiSelect';
import { OpeningClosingFilter } from '@apiHooks/useOpeningClosingFilters';

export type FilterGroupKind = 'segment' | 'cuisine' | 'units';

export interface FilterOptionRef {
    kind: FilterGroupKind;
    name: string;
    value: string;
}

export interface FilterModel {
    groups: GroupedMultiSelectGroup[];
    idToOption: Map<number, FilterOptionRef>;
}

const classifyGroup = (parentName: string): FilterGroupKind => {
    const normalized = parentName.toLowerCase();
    if (normalized.includes('segment')) return 'segment';
    if (normalized.includes('cuisine')) return 'cuisine';
    return 'units';
};

// Option IDs are assigned from a flat counter so there's no collision regardless of group size.
export const buildFilterModel = (filters: OpeningClosingFilter[]): FilterModel => {
    const idToOption = new Map<number, FilterOptionRef>();
    let nextOptionId = 0;

    const groups: GroupedMultiSelectGroup[] = filters.map((filter, groupIndex) => {
        const parentName = filter.parentName ?? '';
        const kind = classifyGroup(parentName);

        const options = (filter.children ?? [])
            .filter(
                (child) =>
                    (child.name ?? '') !== '' &&
                    (kind !== 'units' || (child.value ?? '') !== ''),
            )
            .map((child) => {
                const name = child.name ?? '';
                const value = child.value ?? '';
                const id = nextOptionId;
                nextOptionId += 1;
                idToOption.set(id, { kind, name, value });
                return { id, name };
            });

        return { id: groupIndex, name: parentName, options };
    });

    return { groups, idToOption };
};

export const selectedToQuery = (
    selectedIds: number[],
    idToOption: Map<number, FilterOptionRef>,
): { segments: string[]; cuisines: string[]; chainSizes: string[] } => {
    const segments: string[] = [];
    const cuisines: string[] = [];
    const chainSizes: string[] = [];

    for (const id of selectedIds) {
        const ref = idToOption.get(id);
        if (ref === undefined) continue;
        if (ref.kind === 'segment') segments.push(ref.name);
        else if (ref.kind === 'cuisine') cuisines.push(ref.name);
        else if (ref.kind === 'units' && ref.value !== '') chainSizes.push(ref.value);
    }

    return { segments, cuisines, chainSizes };
};
